(ns fablo-offers.common.components.forms
  (:require ["@chakra-ui/react" :as ch]
            ["react-final-form" :as rff]
            [applied-science.js-interop :as j]
            [cljs-bean.core :refer [->clj ->js] :as bean]
            clojure.string
            [fablo-offers.common.tracking :as tracking]
            [fablo-offers.common.utils :as u]
            [goog.object :as gobj]
            [malli.core :as m]
            [malli.error :as me]
            ["react-icons/fa" :as icons]
            [rumext.alpha :as mf]))

(mf/defc form [params]
  [:> rff/Form
   (-> params
       (dissoc :children)
       (assoc :render
              (mf/fnc
               [{:keys [handleSubmit]}]
               [:form {:on-submit handleSubmit}
                (:children params)]))
       (u/js-camel-case-keys))
   nil])

(defn field-name [x]
  (cond
    (keyword? x) (name x)
    (sequential? x) (clojure.string/join "." (map field-name x))
    :else x
    ;; todo: vectors as [0] in path
    ))

(mf/defc tooltip-icon [{:keys [label]}]
  (let [{:keys [isOpen onOpen onToggle onClose]} (->clj (ch/useDisclosure))]
    [:> ch/Tooltip
     {:is-open isOpen
      :label label
      :white-space :pre-wrap}
     [:span
      [:> ch/Icon
       {:as icons/FaInfoCircle
        :onMouseEnter onOpen
        :onMouseLeave onClose
        :onClick onToggle
        :has-arrow true
        :cursor :pointer
        :ml 2
        :color :gray.500
        :vertical-align :middle}]]]))

(mf/defc field [{:keys [label label-params name cmp-params children component tooltip footnote] :as params}]
  (let [render (mf/fnc
                field-render
                {::mf/wrap-props false}
                [props]
                (let [input (gobj/get props "input")
                      meta (->clj (gobj/get props "meta"))
                      error (when (and (:touched meta) (:error meta))
                              (first (:error meta)))]
                  ;; fix for rff <=> chakra
                  (set! (.-isChecked input) (.-checked input))

                  (mf/with-effect
                    [error]
                    (when error
                      (tracking/track "Field validation error"
                                      {"Field label" label
                                       "Field name" (field-name name)
                                       "Validation error" error
                                       "Field value" (or (.-checked input) (.-value input))})))

                  [:> ch/FormControl
                   {:is-invalid (some? error)
                    :mb (when-not (= (:type params) "hidden") 4)}

                   (when label
                     [:> ch/FormLabel
                      (u/js-camel-case-keys
                       (merge
                        {:html-for (field-name name)
                         :text-align :inherit
                         :font-weight 600
                         :mt 4
                         :mb 4}
                        label-params))
                      label
                      (when tooltip
                        [:& tooltip-icon {:label tooltip}])])
                   (if (and children (not (fn? children)))
                     children
                     [:>
                      (or children component)
                      (let [props #js {}]
                        (js/Object.assign
                         props
                         (u/js-camel-case-keys cmp-params)
                         input)
                        props)])

                   (when footnote
                     [:> ch/FormHelperText
                      {:mt "2px" :ml 1}
                      footnote])

                   (when error
                     [:> ch/FormErrorMessage {:justify-content :center}
                      error])]))]
    [:& rff/Field
     (-> params
         (dissoc :component :children :cmp-params :label-params)
         (update :name field-name)
         (assoc :render render))]))

(mf/defc radio-card [{:keys [option props]}]
  (let [{:keys [getInputProps getCheckboxProps]} (->clj (ch/useRadio props))
        input-props (getInputProps)
        checkbox-props (->clj (getCheckboxProps))
        checked ^js/Boolean (.-isChecked props)]
    [:> ch/Box {:as "label"
                :overflow :visible}
     [:> "input" input-props nil]
     [:> ch/HStack {:w :full}
      [:> ch/Radio
       {:size "lg"
        :is-checked checked
        :on-click (fn [e]
                    (-> e .-target .-parentElement .-parentElement .click))}]
      [:> ch/Box
       #_checkbox-props
       (u/js-camel-case-keys
        (merge
         checkbox-props
         {:border-width "1px"
          :border-color "border-primary.500"
          :border-radius "md"
          :flex 1
          :_focus {:box-shadow "outline"}
          :_checked {:border-width "3px"
                     :box-shadow "md"}}))

       [:> ch/Box {:font-weight 500 :p 3} (:title option)]
       (when (:content option)
         [:> ch/Collapse
          {:in checked
           :animate-opacity true}
          [:*
           [:> ch/Divider {}]
           [:> ch/Box
            {:p 3}
            (:content option)]]])]]]))

(mf/defc radio-subforms [{:keys [props options]}]
  (let [{:keys [getRootProps getRadioProps]} (->clj (ch/useRadioGroup props))
        group (getRootProps)
        _ (gobj/set group "align" "stretch")]
    [:> ch/VStack
     group
     (for [opt options]
       ;^{:key (:value opt)}
       [:& radio-card {:key (:value opt)
                       :option opt
                       :props (getRadioProps #js {:value (:value opt)})}])]))

(mf/defc box-radio
  {::mf/wrap-props false}
  [props]
  (let [items (j/get props :items)
        item-render (j/get props :item-render)
        container-render (j/get props :container-render)
        {:keys [getRootProps getRadioProps]} (->clj (ch/useRadioGroup props))
        radio-props (fn [x] (getRadioProps #js {:value (:value x)
                                                :isDisabled (:disabled x)}))
        group-props (getRootProps)]
    [:> container-render group-props
     (for [x items]
       (let [radio (radio-props x)
             {:keys [getInputProps getCheckboxProps]} (->clj (ch/useRadio radio))]
         [:> ch/Box {:as "label"
                     :flex 1
                     :key (str (:value x))}
          [:> :input (getInputProps)]
          [:& item-render {:item x
                           :props (getCheckboxProps)}]]))]))

(defn errors [t]
  (-> me/default-errors
      (assoc
       ::m/missing-key {:error/message (t "required-field")}
       :string {:error/fn {:en (fn [{:keys [schema value]} _]
                                 (let [{:keys [min max]} (m/properties schema)]
                                   (cond
                                     (not (string? value)) (t "required-field")
                                     (and min (= min max)) (str "should be " min " characters")
                                     (and min max) (str "should be between " min " and " max " characters")
                                     min (str "should be at least " min " characters")
                                     max (str "should be at most " max " characters"))))}}
       :boolean {:error/message (t "required-field")}
       :enum {:error/message (t "required-field")}
       :some {:error/message (t "required-field")}
       ;; fixme, lang to :pl
       ::m/limits {:error/fn {:en (fn [{:keys [schema _value]} _]
                                    (let [{:keys [min max]} (m/properties schema)]
                                      (cond
                                        (and min (= min max)) (str "wybierz " min)
                                        (and min max) (str "wybierz od " min " do " max)
                                        min (str "wybierz co najmniej " min)
                                        max (str "wybierz maksymalnie " max))))}})))

(defn malli-validator [t schema]
  (when schema
    (fn [values]
      ;(js/console.log "malli-validator" schema values)
      (-> schema
          (m/explain (->clj values))
          (me/humanize
           {:errors (errors t)})
          ->js))))

(mf/defc pin-input [{:keys [digits] :as props}]
  [:& ch/PinInput
   props
   (for [i (range (or digits 4))]
     [:> ch/PinInputField {:mr 1
                           :w "40px"
                           :key i}])])
